/**
 * /best-ai-note-taking-app — SEO landing page targeting the
 * "best ai note taking app" / "ai note taking app" / "best ai notes app"
 * keyword cluster.
 *
 * Editorial angle: an HONEST, categorized list of genuinely strong AI
 * note-taking apps — not a thinly-veiled ad. We generously name the real
 * winners in each category (Notion AI, Obsidian, Mem, Reflect, Granola,
 * Otter) and credit what they're actually good at.
 *
 * The twist: Draftlize is NOT a note-taking app, and the page says so
 * plainly. One dedicated section points out that a slice of people landing
 * on a "best AI note app" search aren't actually looking for a note app at
 * all — if what you're "noting" is product decisions that an AI agent must
 * read and write, and that should auto-flag stale when a dependency changes,
 * you're in the wrong category. The honest move is to send those readers to
 * a decision workbench (Draftlize), not to pretend Draftlize is a better
 * notes app. It isn't, and we won't say it is.
 *
 * English-only + inline (no i18n), mirroring the comparison pages.
 * Chinese variant lives in BestAiNoteTakingAppZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function BestAiNoteTakingApp() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/best-ai-note-taking-app"
      documentTitle="Best AI Note-Taking Apps (2026) · Draftlize"
      documentDescription="An honest list of the best AI note taking apps in 2026 — sorted by what you actually capture: all-purpose notes (Notion AI, Obsidian), AI-native notes (Mem, Reflect), and meeting notes (Granola, Otter). Plus when a notes app isn't what you need at all."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">Guide · AI Note-Taking Apps</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(38px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}
          >
            The best AI note-taking apps,<br/>
            <em>and the one case where you don't want one.</em>
          </h1>
          <p className="lede">
            Most "best AI note taking app" lists are a wall of logos. This one isn't. We make a product in this neighborhood, but we'll be upfront: Draftlize is <strong>not</strong> a note-taking app, and we're not going to pretend it's a better one. So this is a genuinely honest map — the strong AI notes apps by what you actually capture, who wins each category, and then the one important case where the thing you're "taking notes" on means a notes app is the wrong tool entirely.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">No single AI notes app wins everything. <em>Pick by what you capture.</em></h2>
          </div>
          <p>"Best AI notes app" depends entirely on what you're noting. A team wiki, a personal second brain, and a wall of meeting transcripts want different tools. Here's who wins each row — and the bottom rows, where the honest answer isn't a notes app at all.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>What you're capturing</th>
              <th>Strong picks</th>
              <th className="ours">Is this a note-app job?</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Team wiki, docs &amp; structured pages</td><td className="yes">Notion AI</td><td className="ours yes">Yes — use Notion</td></tr>
            <tr><td>Personal second brain, linked notes</td><td className="yes">Obsidian, Mem, Reflect</td><td className="ours yes">Yes — use these</td></tr>
            <tr><td>Meeting notes &amp; transcripts</td><td className="yes">Granola, Otter</td><td className="ours yes">Yes — use these</td></tr>
            <tr><td>Quick capture, AI search across notes</td><td className="yes">Mem, Reflect, Notion AI</td><td className="ours yes">Yes — any of these</td></tr>
            <tr><td>Product decisions an AI agent reads &amp; writes</td><td className="no">No notes app does this</td><td className="ours partial">No — that's a decision workbench</td></tr>
            <tr><td>Change one decision, flag what's now stale</td><td className="no">Notes just sit there</td><td className="ours partial">No — you want a dependency graph</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">By category, <em>honestly.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">All-purpose notes</h3>
            <p>
              <strong>Notion AI</strong> — the team-wiki default; flexible databases, collaborative pages, AI drafting and Q&amp;A built right in where your docs already live.<br/>
              <strong>Obsidian</strong> — local-first, plain-text Markdown, link-heavy, beloved by people who think in graphs; AI arrives via plugins, fully under your control.<br/>
              <em>Honest take:</em> if you want one flexible place to write, organize, and search everything, these are genuinely excellent. Hard to go wrong.
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">AI-native notes</h3>
            <p>
              <strong>Mem</strong> — notes that self-organize; you dump thoughts in and AI surfaces and connects them, minimal manual filing.<br/>
              <strong>Reflect</strong> — fast, frictionless networked notes with backlinks and an AI assistant, tuned for daily journaling and quick capture.<br/>
              <em>Honest take:</em> built AI-first from the ground up. If you hate filing and want the tool to do the organizing, these shine. We mean that.
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Meeting notes</h3>
            <p>
              <strong>Granola</strong> — records and enhances your own rough notes into clean meeting summaries without a clunky bot joining the call.<br/>
              <strong>Otter</strong> — real-time transcription, speaker labels, searchable archives, and AI summaries of every meeting.<br/>
              <em>Honest take:</em> if your problem is "I can't write fast enough in meetings," this is exactly the category you want. Draftlize does none of this.
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The one case where a notes app is <em>the wrong tool.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Here's the honest part. Some people searching "best AI note taking app" aren't actually looking for a notes app — they've just never seen the category they need. If that's you, the fix isn't a smarter notes app. It's a different kind of tool.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">What you're "noting" is decisions</h3>
            <p>If the notes you keep re-reading are product decisions — what we're building, why, the rationale, the alternatives we rejected — a notes app stores them as prose that slowly goes out of date. Draftlize captures each as a typed card: decision, rationale, dependencies, addressable by URL and ID. That's not note-taking; that's a decision record.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A change should mark things stale</h3>
            <p>This is the tell that you're out of notes-app territory. A note can't know that a decision it recorded stopped being true — it just sits there, confidently wrong. Draftlize cards know what depends on them: change the pricing decision and every spec built on the old price turns <em>stale</em> automatically, the way a build system invalidates everything downstream of a changed file.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent must read and write it</h3>
            <p>Notes apps are written by you, for you to re-read. Draftlize cards are read <em>and written</em> by AI agents — Claude Code, Cursor — over MCP. The agent drafting your next spec already knows the decisions, and writes new ones back. A notes app can't be the shared, machine-writable source of truth a working agent needs.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          The best AI note-taking app is the one that fits what you capture. If what you capture is product decisions an agent must keep in sync — that's not a notes app at all.
        </blockquote>
        <cite>Draftlize isn't a better notes app. It's a different category: a product decision workbench.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Wrong category? <em>Try the decision workbench free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            If you came here for an AI notes app, keep one of the great ones above — Notion, Obsidian, Mem, Reflect, Granola, Otter. But if what you're really tracking is product decisions that need to stay in sync, Draftlize is the tool that category points to.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
